/* eslint-disable functional/prefer-readonly-type */
/* eslint-disable max-lines-per-function */
/* eslint-disable react/jsx-one-expression-per-line */
import React, { useRef, useEffect, useState } from 'react'
import { useReactToPrint } from 'react-to-print'
import { Button, Container, Grid, Typography } from '@mui/material'
import { useLang } from '~/hooks/useLang'
import { useParams } from 'react-router-dom'
import HeaderSummary from '../components/Summary/HeaderSummary'
import TabSummary from '../components/Summary/TabSummary'
import IndicatorsContentSummary, {
  Data,
  Totals,
} from '../components/Summary/IndicatorsContentSummary'
import { random } from 'lodash'
import ColumnChartHorizontal from '~/components/ColumnChartMui/HorizontalChart'
import ColumnChartVertical from '~/components/ColumnChartMui/VerticalChart'
import PieChartMui from '~/components/PieChartMui'
import DetailsOccurrences from '../components/Summary/DetailsOccurrences'
import Signature from '../components/Summary/Signature'
import RegulatoryObservations from '../components/Summary/RegulatoryObservations'
import {
  getCoordenation,
  getDispatchByCity,
  getDispatchByDispatchGroup,
  getDispatchByType,
  getOccurrenceIndicators,
} from '../services'
import { Downgraded, useHookstate } from '@hookstate/core'

interface paramsProps {
  readonly id: string
}

const cities = [
  { item: 'Campina Grande', value: random(200) },
  { item: 'Santa Rita', value: random(200) },
  { item: 'Bayeux', value: random(200) },
  { item: 'Guarabira', value: random(200) },
  { item: 'Patos', value: random(200) },
  { item: 'Pombal', value: random(200) },
  { item: 'Sousa', value: random(200) },
  { item: 'Cajazeiras', value: random(200) },
  { item: 'Cabedelo', value: random(200) },
  { item: 'Bananeiras', value: random(200) },
]

interface IIndicators {
  readonly city: {
    readonly data: readonly Data[]
    readonly totals: Totals
  }
  readonly operation: {
    readonly data: readonly Data[]
    readonly totals: Totals
  }
}

export interface ICoordenation {
  readonly coordinator: {
    readonly id: number
    readonly name: string
    readonly cpf: string
    readonly rank: {
      readonly name: string | null
    }
    readonly registration: string
    readonly representation_name: string
  }
  readonly date_begin: string
  readonly date_expected_closing: string
  readonly date_end: string
  readonly dispatch_groups: readonly {
    readonly id: number
    readonly name: string
  }[]
}

interface Chart {
  readonly item: string
  readonly value: number
}

export default function Summary() {
  const { translate } = useLang()
  const componentRef = useRef(null)
  const [isPrinting, setIsPrinting] = useState(false)
  const canvasImagesRef = useRef<
    {
      canvas: HTMLCanvasElement
      img: HTMLImageElement
    }[]
  >([])
  const { id }: paramsProps = useParams()
  const indicators = useHookstate<IIndicators | null>(null)
  const coordenation = useHookstate<ICoordenation | null>(null)
  const columnChartByType = useHookstate<readonly Chart[]>([])
  const columnChartByCity = useHookstate<readonly Chart[]>([])
  const pieChartByCity = useHookstate<readonly Chart[]>([])

  const prepareCanvasesForPrint = () => {
    canvasImagesRef.current = []
    const root = componentRef.current as HTMLElement | null
    if (!root) return Promise.resolve()
    const canvases = Array.from(
      root.querySelectorAll('canvas')
    ) as readonly HTMLCanvasElement[]
    canvases.forEach((canvas) => {
      try {
        const dataUrl = canvas.toDataURL()
        const img = document.createElement('img')
        img.src = dataUrl
        img.style.width = canvas.style.width || `${canvas.width}px`
        img.style.height = canvas.style.height || `${canvas.height}px`
        img.className = 'print-replacement'
        canvas.style.display = 'none'
        canvas.parentNode?.insertBefore(img, canvas)
        canvasImagesRef.current.push({ canvas, img })
      } catch (error) {
        // canvas may be tainted by cross-origin resources — ignore
      }
    })
    // Dar tempo para o DOM atualizar antes da impressão
    return new Promise((res) => {
      setIsPrinting(true)
      setTimeout(res, 250)
    })
  }

  const restoreCanvases = () => {
    canvasImagesRef.current.forEach(({ canvas, img }) => {
      img.parentNode?.removeChild(img)
      canvas.style.display = ''
    })
    canvasImagesRef.current = []
    setIsPrinting(false)
  }
  const sumValues = (charts?: readonly Chart[]) =>
    (charts ?? []).reduce((sum, chart) => sum + (chart?.value ?? 0), 0)

  useEffect(() => {
    document.title = translate('Dispatch System')
  }, [])

const pageStyle = `
    @page { size: A4; margin: 0; }  // Alterado de 5mm para 0
    @media print {
      html, body { height: auto !important; overflow: visible !important; margin: 0 !important; padding: 0 !important; }
      .print-container { margin: 0 !important; padding: 0 !important; -webkit-print-color-adjust: exact; }
      .print-container * { margin: 0 !important; padding: 0 !important; overflow: visible !important; }
      /* Removido .page-break para evitar quebras desnecessárias; adicione manualmente se precisar */
      /* Ajustes para tabelas e containers */
      table, thead, tbody, tr, td, th { page-break-inside: avoid; break-inside: avoid; margin: 0 !important; padding: 0 !important; }
      .MuiTableContainer-root { max-height: none !important; overflow: visible !important; margin: 0 !important; padding: 0 !important; }
      /* Ajustes específicos para a tabela de detalhes */
      table { width: 100% !important; table-layout: fixed !important; margin: 0 !important; padding: 0 !important; }
      td, th { word-wrap: break-word !important; overflow-wrap: break-word !important; margin: 0 !important; padding: 0 !important; }
      /* Garantir que canvases sejam visíveis */
      canvas { display: block !important; margin: 0 !important; padding: 0 !important; }
    }
  `

  const handlePrint = useReactToPrint({
    // documentTitle: `Súmula nº: ${overview.get()?.code}`,
    content: () => componentRef.current,
    pageStyle,
    removeAfterPrint: false,
    copyStyles: true,
    onBeforeGetContent: () => prepareCanvasesForPrint(),
    onAfterPrint: () => restoreCanvases(),
  })

  const handleGetCoordination = async () => {
    await getCoordenation(id).then((response) => {
      coordenation.set(response)
    })

    const dispatchGroupIds =
      coordenation.get()?.dispatch_groups.map((group) => group.id) ?? []

    getDispatchByType({
      coordinationId: parseInt(id),
      dispatchGroupIds,
    }).then((response) => {
      return columnChartByType.set(
        response.map((responseData) => ({
          item: responseData.name,
          value: responseData.total,
        }))
      )
    })

    getDispatchByDispatchGroup({
      coordinationId: parseInt(id),
      dispatchGroupIds,
    }).then((response) => {
      return pieChartByCity.set(
        response.map((responseData) => ({
          item: responseData.name,
          value: responseData.total,
        }))
      )
    })

    getDispatchByCity({
      coordinationId: parseInt(id),
      dispatchGroupIds,
    }).then((response) => {
      return columnChartByCity.set(
        response.map((responseData) => ({
          item: responseData.name,
          value: responseData.total,
        }))
      )
    })
  }

  useEffect(() => {
    getOccurrenceIndicators(id).then((response) => {
      indicators.set(response)
    })
    handleGetCoordination()
  }, [])

  return (
    <div>
      <Button onClick={handlePrint}>{translate('Print')}</Button>
      <div
        ref={componentRef}
        className="print-container"
        style={{
          margin: '0',
          padding: '0',
        }}
      >
        {/* removida page-break inicial para evitar página em branco antes da primeira */}
        <HeaderSummary headerSummary={coordenation.get()} />
        <TabSummary title={translate('Indicators by city')}>
          <IndicatorsContentSummary
            groupedBy="Cities"
            indicator={indicators.get()?.city}
          />
        </TabSummary>
        <TabSummary title={translate('Indicators by operation')}>
          <IndicatorsContentSummary
            groupedBy="Operation"
            indicator={indicators.get()?.operation}
          />
        </TabSummary>
        <Container fixed={false} sx={{ marginTop: 5, textAlign: 'center' }}>
          <Typography variant="caption">
            {translate('OCS - Occurrences | ADA - Adults arrested | MNA - Minors apprehended | AMA - Weapons seized')}
          </Typography>
          <br />
          <Typography variant="caption">
            {translate('MUA - Ammunition seized | DGA - Drugs seized | VGA - Vehicles seized | VER - Vehicles recovered')}
          </Typography>
        </Container>
        <TabSummary title={translate('Types of occurrences')}>
          <Container fixed={false} sx={{ border: 'none' }}>
            {columnChartByType.get().length ? (
              <ColumnChartHorizontal
                data={columnChartByType.attach(Downgraded).get()}
                title={translate('Occurrences x Type')}
                subtitle={`${translate('Total')}: ${sumValues(
                  columnChartByType.get()
                )} | ${translate('Total types')}: ${
                  columnChartByType.get().length
                }`}
                colorBar={'primary'}
                heightFit
                isPrinting={isPrinting}
              />
            ) : (
              <Grid item xs={12} textAlign={'center'}>
                <Typography variant="subtitle1">
                  <b>{translate('NO INFORMATION REGISTERED')}</b>
                </Typography>
              </Grid>
            )}
          </Container>
        </TabSummary>
        <div className="page-break" />
        <TabSummary title={translate('Dispatch groups')}>
          <Container fixed={false}>
            {pieChartByCity.get().length ? (
              <ColumnChartHorizontal
                data={pieChartByCity.attach(Downgraded).get()}
                title={translate('Occurrences x Dispatch Groups')}
                subtitle={`${translate('Total')}: ${sumValues(
                  pieChartByCity.get()
                )} | ${translate('Total groups')}: ${
                  pieChartByCity.get().length
                }`}
                colorBar={'secondary'}
                heightFit
                isPrinting={isPrinting}
              />
            ) : (
              <Grid item xs={12} textAlign={'center'}>
                <Typography variant="subtitle1">
                  <b>{translate('NO INFORMATION REGISTERED')}</b>
                </Typography>
              </Grid>
            )}
          </Container>
        </TabSummary>
        <TabSummary title={translate('Cities')}>
          <Container fixed={false}>
            {columnChartByCity.get().length ? (
              <ColumnChartHorizontal
                data={columnChartByCity.attach(Downgraded).get()}
                title={translate('Occurrences x Cities')}
                subtitle={`${translate('Total')}: ${sumValues(
                  columnChartByCity.get()
                )} | ${translate('Total cities')}: ${
                  columnChartByCity.get().length
                }`}
                colorBar={'tertiary'}
                heightFit
                isPrinting={isPrinting}
              />
            ) : (
              <Grid item xs={12} textAlign={'center'}>
                <Typography variant="subtitle1">
                  <b>{translate('NO INFORMATION REGISTERED')}</b>
                </Typography>
              </Grid>
            )}
          </Container>
        </TabSummary>
        <div className="page-break" />
        <TabSummary title={translate('Occurrence details')}>
          <DetailsOccurrences id={id} />
        </TabSummary>
        <div className="page-break" />
        <TabSummary title={translate('Regulatory observations')}>
          <RegulatoryObservations id={id} />
        </TabSummary>
        <Signature coordenation={coordenation.get()} />
      </div>
    </div>
  )
}
